import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Search, RefreshCw, AlertTriangle, Users } from "lucide-react";
import api from "../lib/api";
import Header from "../components/dashboard/Header";
import MemberDetail, { StatusPill, money } from "../components/MemberDetail";

const timeAgo = (iso) => { if (!iso) return "never"; const m = Math.round((Date.now() - new Date(iso)) / 60000); return m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`; };

const officeList = (offices) => {
  const nums = offices.map((o) => o.number);
  return nums.length > 4 ? `${nums.slice(0, 4).join(", ")} +${nums.length - 4} more` : nums.join(", ");
};

const TYPE_LABEL = { office: "Office", coworking: "Coworking", virtual: "Virtual" };
const typeLabel = (m) => (m.status === "former" ? "Former member" : TYPE_LABEL[m.member_type] || "Coworking");
const fmtEnd = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

function MemberRow({ m, onOpen }) {
  return (
    <tr onClick={() => onOpen(m.company_id)} data-testid={`member-row-${m.company_id}`} className={`border-b border-brand-border last:border-0 hover:bg-brand-hover cursor-pointer transition-colors ${m.status === "former" ? "opacity-70" : ""}`}>
      <td className="px-5 py-3.5">
        <p className="text-sm font-medium text-brand-ink flex items-center gap-1.5">{m.company_name}
          {m.billing?.delinquent && <AlertTriangle className="h-3.5 w-3.5 text-rose-600" title="Delinquent" />}</p>
        <p className="text-xs text-brand-ink sm:hidden">{officeList(m.offices) || typeLabel(m)} · {m.locations.join(", ")}{m.contract_end ? ` · ${m.status === "former" ? "left" : "ends"} ${fmtEnd(m.contract_end)}` : ""}</p>
        <p className="text-xs text-brand-muted truncate max-w-[200px] sm:max-w-xs">{m.primary_contact?.name || m.contacts[0]?.name || ""}{m.primary_contact?.email ? ` · ${m.primary_contact.email}` : ""}</p>
      </td>
      <td className="px-5 py-3.5 text-sm text-brand-ink hidden sm:table-cell">
        {officeList(m.offices) || <span className="text-brand-tertiary" data-testid={m.member_type === "virtual" ? `member-type-virtual-${m.company_id}` : undefined}>{typeLabel(m)}</span>}
      </td>
      <td className="px-5 py-3.5 text-sm text-brand-muted hidden md:table-cell">{m.locations.join(", ")}</td>
      <td className="px-5 py-3.5 text-sm text-brand-muted hidden lg:table-cell">{m.contact_count}</td>
      <td className="px-5 py-3.5 text-sm text-brand-muted hidden lg:table-cell whitespace-nowrap" data-testid={`member-end-${m.company_id}`}>{m.status === "former" ? `Left ${fmtEnd(m.contract_end)}` : fmtEnd(m.contract_end)}</td>
      <td className="px-5 py-3.5 text-sm text-brand-ink text-right whitespace-nowrap">{m.status === "former" ? "—" : money(m.mrr)}</td>
      <td className="px-5 py-3.5 hidden sm:table-cell">{m.status === "former" ? <span className="text-[10px] uppercase tracking-[0.12em] text-brand-tertiary">Former</span> : <StatusPill status={m.status} />}</td>
    </tr>
  );
}

export default function Members() {
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [location, setLocation] = useState(() => new URLSearchParams(window.location.search).get("location") || "all");
  const [status, setStatus] = useState("all");
  const [memberType, setMemberType] = useState("all");
  const [data, setData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(null);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const id = ++seq.current;
    try {
      const { data: d } = await api.get("/members", { params: { q, location, status, member_type: memberType } });
      if (id === seq.current) setData(d);
    } catch (e) { if (id === seq.current) { setData({ members: [], sync: {} }); toast.error("Members couldn't load", { description: e.userMessage, id: "members" }); } }
  }, [q, location, status, memberType]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/bookings/config").then(({ data: d }) => setLocations((d.mappings || []).filter((m) => m.property_id).map((m) => m.location))).catch(() => {}); }, []);
  useEffect(() => { if (!data?.sync?.running) return; const t = setInterval(load, 5000); return () => clearInterval(t); }, [data?.sync?.running, load]);

  const refresh = async () => {
    try { await api.post("/members/sync"); toast.success("Refreshing from Yardi — this takes a few minutes"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start refresh"); }
  };

  const rows = data?.members || [];
  const sync = data?.sync || {};
  return (
    <div className="min-h-screen bg-brand-page">
      <Header />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-brand-tertiary mb-2">Operations</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-brand-ink tracking-tight" data-testid="members-title">Member Lookup</h1>
            <p className="text-sm text-brand-muted mt-2">Look up current and former members from Yardi: offices, contract end dates, contacts and billing. Search a person's name for mail sorting.</p>
          </div>
          <div className="text-right">
            <button onClick={refresh} disabled={sync.running} data-testid="members-refresh"
              className="inline-flex items-center gap-2 rounded-lg border border-brand-border bg-brand-surface px-4 py-2 text-sm text-brand-ink hover:bg-brand-hover disabled:opacity-60 transition-colors">
              <RefreshCw className={`h-4 w-4 ${sync.running ? "animate-spin" : ""}`} /> {sync.running ? "Refreshing…" : "Refresh from Yardi"}
            </button>
            <p className="text-[11px] text-brand-tertiary mt-1.5" data-testid="members-sync-info">{sync.running ? sync.progress : `Updated ${timeAgo(data?.synced_at)}`}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-5 rounded-xl border border-brand-border bg-brand-surface p-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-tertiary" />
            <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus data-testid="members-search"
              placeholder="Search company, person, email, phone or office #"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-brand-border bg-brand-page text-brand-ink outline-none focus:border-brand-primary" />
          </div>
          <select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="members-location" className="text-sm rounded-lg border border-brand-border bg-brand-page px-3 py-2 text-brand-ink">
            <option value="all">All locations</option>
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="members-status" className="text-sm rounded-lg border border-brand-border bg-brand-page px-3 py-2 text-brand-ink">
            <option value="all">Current members</option>
            <option value="active">Active</option>
            <option value="notice">On notice</option>
            <option value="upcoming">Starting soon</option>
            <option value="former">Former members</option>
            <option value="everyone">Current + former</option>
          </select>
          <select value={status === "former" ? "all" : memberType} disabled={status === "former"} onChange={(e) => setMemberType(e.target.value)} data-testid="members-type" title={status === "former" ? "Type isn't known for former members" : undefined} className="text-sm rounded-lg border border-brand-border bg-brand-page px-3 py-2 text-brand-ink disabled:opacity-50">
            <option value="all">All types</option>
            <option value="office">Private office</option>
            <option value="coworking">Coworking</option>
            <option value="virtual">Virtual</option>
          </select>
          <span className="text-xs text-brand-tertiary px-1" data-testid="members-count">{rows.length} members</span>
        </div>

        <div className="rounded-xl border border-brand-border bg-brand-surface overflow-hidden">
          {data === null ? (
            <div className="p-5 space-y-3">{[...Array(6)].map((_, i) => <div key={i} className="h-12 rounded bg-brand-hover animate-pulse" />)}</div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center" data-testid="members-empty">
              <Users className="h-8 w-8 text-brand-tertiary mx-auto mb-3" />
              <p className="text-sm text-brand-muted">{sync.running ? "Pulling members from Yardi for the first time…" : "No members match your search."}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="members-table">
                <thead>
                  <tr className="border-b border-brand-border text-left text-[10px] uppercase tracking-[0.14em] text-brand-tertiary">
                    <th className="px-5 py-3 font-normal">Company</th>
                    <th className="px-5 py-3 font-normal hidden sm:table-cell">Office / type</th>
                    <th className="px-5 py-3 font-normal hidden md:table-cell">Location</th>
                    <th className="px-5 py-3 font-normal hidden lg:table-cell">Contacts</th>
                    <th className="px-5 py-3 font-normal hidden lg:table-cell">Contract ends</th>
                    <th className="px-5 py-3 font-normal text-right">Monthly</th>
                    <th className="px-5 py-3 font-normal hidden sm:table-cell">Status</th>
                  </tr>
                </thead>
                <tbody>{rows.map((m) => <MemberRow key={m.company_id} m={m} onOpen={setOpen} />)}</tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      {open && <MemberDetail companyId={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
